import {
  Flex,
  Box,
  Text,
  Heading,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  Users,
  CheckCircle,
  XCircle,
  TrendingUp,
  Target,
  ListTodo,
  AlertTriangle,
} from 'lucide-react';
import type { DashboardStatsCardsProps } from '../dashboard.types';

/**
 * Dashboard Statistics Cards Component
 * Displays seven stat cards in horizontal layout: Total Rocks, On Track, Off Track,
 * Progress, Milestones, Action Items, Company Issues
 */
export function DashboardStatsCards({
  stats,
  onMilestonesClick,
  onActionItemsClick,
  onCompanyIssuesClick,
}: DashboardStatsCardsProps) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const bgGreen = useColorModeValue('green.50', 'green.900');
  const bgRed = useColorModeValue('red.50', 'red.900');
  const bgBlue = useColorModeValue('blue.50', 'blue.900');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const dividerColor = useColorModeValue('gray.200', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverRedBg = useColorModeValue('red.100', 'red.800');
  const hoverYellowBg = useColorModeValue('yellow.50', 'yellow.900');

  return (
    <Flex
      bg={cardBg}
      rounded="lg"
      borderWidth="1px"
      borderColor={borderColor}
      shadow="sm"
    >
      {/* Total Rocks */}
      <Box flex="1" px={4} py={2.5} textAlign="center" borderRightWidth="1px" borderColor={dividerColor}>
        <Icon as={Users} w={5} h={5} color="blue.500" mx="auto" mb={1} />
        <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={0.5}>
          Total
        </Text>
        <Heading size="lg" color={headingColor}>
          {stats.total}
        </Heading>
      </Box>

      {/* On Track */}
      <Box flex="1" px={4} py={2.5} textAlign="center" bg={bgGreen} borderRightWidth="1px" borderColor={dividerColor}>
        <Icon as={CheckCircle} w={5} h={5} color="green.500" mx="auto" mb={1} />
        <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={0.5}>
          On Track
        </Text>
        <Heading size="lg" color="green.600">
          {stats.onTrack}
        </Heading>
      </Box>

      {/* Off Track */}
      <Box
        flex="1"
        px={4}
        py={2.5}
        textAlign="center"
        bg={stats.offTrack > 0 ? bgRed : 'transparent'}
        borderRightWidth="1px"
        borderColor={dividerColor}
      >
        <Icon as={XCircle} w={5} h={5} color="red.500" mx="auto" mb={1} />
        <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={0.5}>
          Off Track
        </Text>
        <Heading size="lg" color="red.600">
          {stats.offTrack}
        </Heading>
      </Box>

      {/* Progress */}
      <Box flex="1" px={4} py={2.5} textAlign="center" bg={bgBlue} borderRightWidth="1px" borderColor={dividerColor}>
        <Icon as={TrendingUp} w={5} h={5} color="blue.500" mx="auto" mb={1} />
        <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={0.5}>
          Progress
        </Text>
        <Heading size="lg" color="blue.600">
          {stats.avgProgress}%
        </Heading>
      </Box>

      {/* Milestones */}
      <Box
        flex="1"
        px={4}
        py={2.5}
        textAlign="center"
        bg={stats.milestones.overdue > 0 ? bgRed : 'transparent'}
        cursor={stats.milestones.overdue > 0 ? 'pointer' : 'default'}
        onClick={stats.milestones.overdue > 0 ? onMilestonesClick : undefined}
        _hover={stats.milestones.overdue > 0 ? { bg: hoverRedBg } : {}}
        transition="background-color 0.2s"
        borderRightWidth="1px"
        borderColor={dividerColor}
      >
        {stats.milestones.overdue > 0 ? (
          <Icon as={XCircle} w={5} h={5} color="red.500" mx="auto" mb={1} />
        ) : (
          <Icon as={Target} w={5} h={5} color="orange.500" mx="auto" mb={1} />
        )}
        <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={0.5}>
          Milestones
        </Text>
        <Heading size="lg" color={stats.milestones.overdue > 0 ? 'red.600' : headingColor}>
          {stats.milestones.total}
        </Heading>
        {stats.milestones.overdue > 0 && (
          <Text fontSize="xs" color="red.600" fontWeight="medium">
            {stats.milestones.overdue} overdue
          </Text>
        )}
      </Box>

      {/* Action Items */}
      <Box
        flex="1"
        px={4}
        py={2.5}
        textAlign="center"
        bg={stats.actionItems.overdue > 0 ? bgRed : 'transparent'}
        cursor={stats.actionItems.overdue > 0 ? 'pointer' : 'default'}
        onClick={stats.actionItems.overdue > 0 ? onActionItemsClick : undefined}
        _hover={stats.actionItems.overdue > 0 ? { bg: hoverRedBg } : {}}
        transition="background-color 0.2s"
        borderRightWidth="1px"
        borderColor={dividerColor}
      >
        {stats.actionItems.overdue > 0 ? (
          <Icon as={XCircle} w={5} h={5} color="red.500" mx="auto" mb={1} />
        ) : (
          <Icon as={ListTodo} w={5} h={5} color="teal.500" mx="auto" mb={1} />
        )}
        <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={0.5}>
          Action Items
        </Text>
        <Heading size="lg" color={stats.actionItems.overdue > 0 ? 'red.600' : headingColor}>
          {stats.actionItems.total}
        </Heading>
        {stats.actionItems.overdue > 0 && (
          <Text fontSize="xs" color="red.600" fontWeight="medium">
            {stats.actionItems.overdue} overdue
          </Text>
        )}
      </Box>

      {/* Company Issues */}
      <Box
        flex="1"
        px={4}
        py={2.5}
        textAlign="center"
        cursor="pointer"
        onClick={onCompanyIssuesClick}
        _hover={{ bg: hoverYellowBg }}
        transition="background-color 0.2s"
      >
        <Icon as={AlertTriangle} w={5} h={5} color="yellow.600" mx="auto" mb={1} />
        <Text fontSize="xs" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={0.5}>
          Company Issues
        </Text>
        <Heading size="lg" color={headingColor}>
          {stats.companyIssues.total}
        </Heading>
      </Box>
    </Flex>
  );
}

